import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import Icon from '../misc/Icon'
import Label from '../misc/Label'
import { TWEEN } from 'three/examples/jsm/libs/tween.module.min'
import { appPrimaryColor, houseText } from '../Store'
import Button from '../misc/Button'

const gltfLoader = new GLTFLoader()

export default class HouseScene extends THREE.Object3D {
    constructor(controls, camera) {
        super()
        this.camera = camera
        this.controls = controls
        this.previousPositions = []
        this.create()
        this.index = 0
        this.lineP = new THREE.Vector3()
        this.text = ['ar', '5gbox', 'phone', 'headset']
    }

    async create() {
        const house = await this.loadHouse()
        this.add(house)
        this.dispatchEvent({ type: 'loaded' })
        this.loadLabel()
        this.loadLine()
        this.addBindings()
        this.addEventListeners()
    }

    addBindings() {
        this.onClick = this.onClick.bind(this)
    }

    addEventListeners() {
        this.controls.addEventListener('selectStart', this.onClick)
    }

    removeEventListeners() {
        this.controls.removeEventListener('selectStart', this.onClick)
    }

    onClick(event) {
        const evt = event.data

        if (evt && evt.intersect) {
            const intersect = checkChildren(this.modelChildren, evt.intersect)
            if (!intersect || !intersect.object) return
            for (let index = 0; index < this.modelChildren.length; index++) {
                const model = this.modelChildren[index];
                const intersectObject = intersect.object
                if (intersectObject.uuid === model.uuid || intersectObject.parent.uuid === model.uuid) {
                    this.index = index
                    this.play()
                    clearInterval(this.playInterval)
                }

            }
        }
    }

    tick() {
        if (!this.echo) return
        for (const line of this.lines) {
            const points = [];
            const worldPosition = line.child.getWorldPosition(this.lineP)
            const calculatedWorldP = worldPosition.subVectors(worldPosition, this.position)
            points.push(this.calculatedEchoWorldP);
            points.push(calculatedWorldP);
            line.geometry.setFromPoints(points);
        }
    }

    async loadHouse() {
        const house = await gltfLoader.loadAsync('https://pivotal-5g-webxr.s3.amazonaws.com/assets/house4.glb')
        const houseModel = house.scene
        const scale = 0.8
        houseModel.scale.set(scale, scale, scale)
        houseModel.position.y = 0.9
        houseModel.rotateY(-Math.PI)
        this.model = houseModel
        console.log({ houseModel })
        const echoOld = houseModel.children[0].children.find(v => v.name === "ECHO_mimo")
        echoOld.removeFromParent()

        const glasses = houseModel.children.find(v => v.name === "Glasses")
        const cube = houseModel.children.find(v => v.name === "Cube")
        const phone = houseModel.children.find(v => v.name === "Phone")
        const vrHeadset = houseModel.children.find(v => v.name === "Htc_vive")
        this.modelChildren = [glasses, cube, phone, vrHeadset]

        // houseModel.children[3].position.set(-0.4, 0.5, 1.45)

        houseModel.children[0].traverse(child => {
            if (child.name === "Scene006_1") {
                child.material.color = new THREE.Color("rgb(90,150,250)")
                child.material.transparent = true
                child.material.opacity = 0.2
            }

            if (child.name === "Floor") {
                const texture = child.material.map
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(3, 3);
            }
        })
        for (const child of this.modelChildren) {
            const newScale = child.scale.x * 1.4
            child.scale.set(newScale, newScale, newScale)
            this.controls.intersectableObjects = [...this.controls.intersectableObjects, child]
        }

        const echo = houseModel.children[2]
        this.echo = echo

        await this.loadIcons(houseModel, house)
        return houseModel
    }

    loadLine() {
        const echoWorldP = this.echo.getWorldPosition(new THREE.Vector3())
        this.calculatedEchoWorldP = echoWorldP.subVectors(echoWorldP, this.position)
        let lines = []
        const material = new THREE.LineDashedMaterial({
            color: appPrimaryColor,
            linewidth: 5,
            scale: 1,
            dashSize: 0.1,
            gapSize: 0.05,
        });
        for (const child of this.modelChildren) {
            //create a blue LineBasicMaterial
            const worldPosition = child.getWorldPosition(this.lineP)
            const calculatedWorldP = worldPosition.subVectors(worldPosition, this.position)
            const points = [];
            points.push(this.calculatedEchoWorldP);
            points.push(calculatedWorldP);
            const geometry = new THREE.BufferGeometry().setFromPoints(points);
            const line = new THREE.Line(geometry, material);
            line.computeLineDistances();
            line.scale.set(1, 1, 1);
            line.name = child.name + '-line'
            this.add(line);
            line.child = child
            lines.push(line)
        }
        this.lines = lines
    }
    async loadIcons(model, gltf) {
        const playButton = new Icon(this.controls, { src: 'https://pivotal-5g-webxr.s3.amazonaws.com/assets/Play.png', color: 'white', height: 0.25, width: 0.25 })
        const rewindButton = new Icon(this.controls, { src: 'https://pivotal-5g-webxr.s3.amazonaws.com/assets/Rewind.png', color: 'white', height: 0.25, width: 0.25 })

        this.add(playButton)
        this.add(rewindButton)

        rewindButton.position.set(-0.125, 1, -0.5)
        playButton.position.set(0.125, 1, -0.5)

        for (const child of this.modelChildren) {
            this.previousPositions = [...this.previousPositions, child.position.clone()]
        }


        playButton.addEventListener('click', () => {
            resetIcons()
            playButton.setColor(appPrimaryColor)
            this.play()
            clearInterval(this.playInterval)
            this.playInterval = setInterval(() => {
                this.play()
            }, 7000)
            setTimeout(resetIcons, 500)
        })
        rewindButton.addEventListener('click', () => {
            resetIcons()
            rewindButton.setColor(appPrimaryColor)
            this.reset()
            setTimeout(resetIcons, 500)
            this.index = 0
            this.label.visible = false
            clearInterval(this.playInterval)
        })

        const resetIcons = () => {
            playButton.setColor('white')
            rewindButton.setColor('white')
        }
    }

    play() {
        this.reset()
        setTimeout(() => {
            this.label.visible = true
        }, 500)
        const child = this.modelChildren[this.index]
        houseText.set(this.text[this.index])
        
        new TWEEN.Tween(child.position)
            .to(new THREE.Vector3(0, 1.25, -0.5), 1500)
            .easing(TWEEN.Easing.Cubic.InOut)
            .start();

        if (child.name === "Phone") {
            new TWEEN.Tween(child.rotation)
                .to(new THREE.Vector3(0, 0, -Math.PI), 1500)
                .easing(TWEEN.Easing.Cubic.InOut)
                .start();
        }

        if (this.index >= this.modelChildren.length - 1) {
            this.index = 0
        } else {
            this.index += 1
        }
    }


    reset() {
        const phone = this.modelChildren.find(v => v.name === "Phone")
        new TWEEN.Tween(phone.rotation)
            .to(new THREE.Vector3(Math.PI / 2, 0, -2.3911), 1500)
            .easing(TWEEN.Easing.Cubic.InOut)
            .start();

        for (let index = 0; index < this.modelChildren.length; index++) {
            const child = this.modelChildren[index];
            const position = this.previousPositions[index]
            new TWEEN.Tween(child.position)
                .to(new THREE.Vector3(position.x, position.y, position.z), 500)
                .easing(TWEEN.Easing.Cubic.InOut)
                .start();
        }
    }


    loadLabel() {
        const deviceLabel = new Label('#houseScene', 0.5, 1)
        houseText.subscribe(v => {
            try {
                setTimeout(() => {
                    deviceLabel.update()
                }, 50)
            } catch (e) {
                console.log(e)
            }
        })
        this.add(deviceLabel)
        this.label = deviceLabel
        deviceLabel.visible = false
        deviceLabel.position.set(0, 1.5, -0.5)

        const windowLabel = new Label('#windowScene', 1, 2)
        windowLabel.position.set(2.5, 2.35, -2)
        windowLabel.rotateY(-Math.PI / 4)
        this.add(windowLabel)
    }
}


function checkChildren(children, intersect) {
    let intersecting = false
    for (const child of children) {
        if (intersect.object.uuid === child.uuid) intersecting = intersect
        if (!intersecting && child.children) intersecting = checkChildren(child.children, intersect)
    }
    return intersecting
}